/**
 * 存储清理和迁移服务
 * 自动清理localStorage，迁移数据到新存储
 * 完全后台运行，不阻塞UI
 */

import storageManager from './storageManager'

class StorageCleanupService {
  constructor() {
    this.isCleanupRunning = false
    this.cleanupCompleted = false
    this.hasShownWarning = false
  }

  /**
   * 启动时自动清理（完全异步，不阻塞UI）
   * 注意：现在主要使用IndexedDB，localStorage仅用于少量配置数据
   */
  async autoCleanup() {
    // 避免重复清理
    if (this.isCleanupRunning || this.cleanupCompleted) {
      return
    }

    this.isCleanupRunning = true
    console.log('🧹 启动自动存储清理...')

    try {
      // 1. 检查localStorage使用率
      const usage = this.checkUsage()
      console.log(`📊 localStorage使用率: ${usage.percent.toFixed(1)}% (${usage.usedMB}MB / ${usage.maxMB}MB)`)
      console.log(`💡 提示：大数据已迁移到IndexedDB（容量几乎无限），localStorage仅用于配置`)

      // 2. 使用率正常，跳过清理
      if (usage.percent < 80) {
        console.log('✅ localStorage使用率正常，无需清理')
        this.cleanupCompleted = true
        this.isCleanupRunning = false
        return
      }

      // 3. 使用率过高，启动后台清理
      console.log('⚠️ localStorage使用率过高，启动后台清理...')
      console.log('💾 主要数据已使用IndexedDB存储，不受影响')
      
      // 使用setTimeout确保完全异步，不阻塞页面加载
      setTimeout(async () => {
        await this.performCleanup()
        this.isCleanupRunning = false
        this.cleanupCompleted = true
      }, 1000) // 延迟1秒，确保页面已完全加载

    } catch (error) {
      console.error('❌ 自动清理失败:', error)
      this.isCleanupRunning = false
    }
  }

  /**
   * 执行清理操作
   */
  async performCleanup() {
    try {
      console.log('🔄 开始清理localStorage...')

      // 1. 识别需要保留的键
      const keysToKeep = [
        'altverse-auth-store',      // 认证信息
        'altverse-settings',         // 用户设置
        'theme',                     // 主题
        'language',                  // 语言
        'user-preferences'           // 用户偏好
      ]

      // 2. 识别需要迁移的数据
      const keysToMigrate = [
        'altverse-story-store',      // 旧的故事数据
        'altverse-stories-',         // 用户故事（带用户ID）
      ]

      // 3. 扫描所有键
      const allKeys = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key) allKeys.push(key)
      }

      console.log(`📋 发现 ${allKeys.length} 个localStorage项`)

      // 4. 迁移重要数据到新存储
      let migratedCount = 0
      for (const key of allKeys) {
        // 检查是否需要迁移
        const shouldMigrate = keysToMigrate.some(pattern => key.includes(pattern))
        
        if (shouldMigrate) {
          try {
            const data = localStorage.getItem(key)
            if (data) {
              // 迁移到sessionStorage
              sessionStorage.setItem(key, data)
              console.log(`✅ 已迁移: ${key} (${(data.length / 1024).toFixed(2)}KB)`)
              migratedCount++
            }
          } catch (error) {
            console.warn(`⚠️ 迁移失败: ${key}`, error)
          }
        }
      }

      console.log(`✅ 已迁移 ${migratedCount} 项数据`)

      // 5. 清理不需要的数据
      let cleanedCount = 0
      let freedSpace = 0

      for (const key of allKeys) {
        // 检查是否需要保留
        const shouldKeep = keysToKeep.some(pattern => key.includes(pattern))
        
        if (!shouldKeep) {
          try {
            const data = localStorage.getItem(key)
            const size = data ? data.length : 0
            
            localStorage.removeItem(key)
            
            cleanedCount++
            freedSpace += size
            
            // 分批清理，避免阻塞
            if (cleanedCount % 10 === 0) {
              await new Promise(resolve => setTimeout(resolve, 10))
            }
          } catch (error) {
            console.warn(`⚠️ 清理失败: ${key}`, error)
          }
        }
      }

      console.log(`✅ 清理完成: 删除 ${cleanedCount} 项，释放 ${(freedSpace / 1024 / 1024).toFixed(2)}MB`)

      // 6. 显示清理后的状态
      const newUsage = this.checkUsage()
      console.log(`📊 清理后使用率: ${newUsage.percent.toFixed(1)}% (${newUsage.usedMB}MB / ${newUsage.maxMB}MB)`)

      // 7. 如果仍然很高，给出警告
      if (newUsage.percent > 70) {
        console.warn('⚠️ localStorage仍然使用率较高，建议手动清空')
        this.showWarning()
      }

      return {
        cleaned: cleanedCount,
        migrated: migratedCount,
        freedMB: (freedSpace / 1024 / 1024).toFixed(2),
        newUsage: newUsage.percent
      }
    } catch (error) {
      console.error('❌ 清理过程出错:', error)
      throw error
    }
  }

  /**
   * 检查localStorage使用率
   */
  checkUsage() {
    try {
      const used = JSON.stringify(localStorage).length
      const max = 5 * 1024 * 1024 // 5MB
      const percent = (used / max) * 100

      return {
        used: used,
        usedMB: (used / 1024 / 1024).toFixed(2),
        max: max,
        maxMB: (max / 1024 / 1024).toFixed(2),
        percent: percent
      }
    } catch (error) {
      console.error('检查使用率失败:', error)
      return { used: 0, usedMB: 0, max: 5242880, maxMB: 5, percent: 0 }
    }
  }

  /**
   * 显示警告（只显示一次）
   */
  showWarning() {
    if (this.hasShownWarning) return
    this.hasShownWarning = true

    console.warn(`
╔═══════════════════════════════════════════════════════╗
║  ⚠️  localStorage 仍然使用率较高                      ║
║                                                        ║
║  建议手动清空localStorage（会丢失登录状态）：         ║
║  1. 打开浏览器控制台（F12）                           ║
║  2. 执行：localStorage.clear()                        ║
║  3. 刷新页面：location.reload()                       ║
║                                                        ║
║  或者继续使用，新数据会保存到sessionStorage/IndexedDB ║
╚═══════════════════════════════════════════════════════╝
    `)
  }

  /**
   * 完全清空localStorage（谨慎使用）
   */
  async clearAll() {
    try {
      console.warn('⚠️ 清空所有localStorage...')
      localStorage.clear()
      console.log('✅ localStorage已清空')
      return true
    } catch (error) {
      console.error('❌ 清空失败:', error)
      return false
    }
  }

  /**
   * 获取清理建议
   */
  getCleanupAdvice() {
    const usage = this.checkUsage()

    if (usage.percent < 30) {
      return {
        level: 'good',
        message: '✅ 存储状态良好',
        action: 'none'
      }
    } else if (usage.percent < 70) {
      return {
        level: 'warning',
        message: '⚠️ 存储使用率偏高，建议清理',
        action: 'suggest'
      }
    } else {
      return {
        level: 'critical',
        message: '🚨 存储使用率过高，强烈建议清理',
        action: 'required'
      }
    }
  }

  /**
   * 禁用localStorage写入（拦截setItem）
   */
  disableLocalStorageWrites() {
    // 保存原始的setItem方法
    const originalSetItem = localStorage.setItem.bind(localStorage)

    // 重写setItem方法
    localStorage.setItem = function(key, value) {
      // 只允许白名单键写入
      const whitelist = [
        'altverse-auth-store',
        'altverse-settings',
        'theme',
        'language'
      ]

      const isAllowed = whitelist.some(pattern => key.includes(pattern))

      if (isAllowed) {
        // 允许写入，但检查大小
        try {
          originalSetItem(key, value)
        } catch (error) {
          console.warn(`⚠️ localStorage写入失败（空间不足）: ${key}`)
          // 降级到sessionStorage
          try {
            sessionStorage.setItem(key, value)
            console.log(`✅ 已降级到sessionStorage: ${key}`)
          } catch (sessionError) {
            console.error('sessionStorage也写入失败:', sessionError)
          }
        }
      } else {
        // 不允许写入，自动降级到sessionStorage
        console.warn(`⚠️ 阻止localStorage写入（已降级到sessionStorage）: ${key}`)
        try {
          sessionStorage.setItem(key, value)
        } catch (error) {
          console.error('sessionStorage写入失败:', error)
        }
      }
    }

    console.log('🔒 localStorage写入已限制，仅允许白名单键')
  }
}

// 创建全局单例
const storageCleanupService = new StorageCleanupService()

// 不再禁用localStorage（改用IndexedDB存储大数据）
if (typeof window !== 'undefined') {
  // 只打印提示信息，不限制localStorage
  console.log('💾 存储架构：IndexedDB（主要数据） + localStorage（配置）')
  // storageCleanupService.disableLocalStorageWrites() // 已禁用限制
}

export default storageCleanupService



